import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Rating,
  Button,
  Collapse,
  Divider,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Verified as VerifiedIcon,
  Reply as ReplyIcon,
  Image as ImageIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '../../services/api';

/**
 * Composant réutilisable pour afficher les reviews d'un listing
 * avec pagination infinie (Load More)
 * 
 * @param {string} listingId - ID du listing
 * @param {number} initialLimit - Nombre de reviews à charger initialement (défaut: 5)
 */
const ListingReviewsList = ({ listingId, initialLimit = 5 }) => {
  const { t, i18n } = useTranslation(['reviews', 'common']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // États
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [expandedReviews, setExpandedReviews] = useState(new Set());
  const [pagination, setPagination] = useState({
    page: 1,
    limit: initialLimit,
    total: 0,
    totalPages: 0
  });

  // Charger les reviews initiales
  useEffect(() => {
    console.log('[ListingReviewsList] listingId received:', listingId);
    if (listingId) {
      loadReviews(1, true);
    } else {
      // Si pas de listingId, on s'arrête en mode "loading false"
      setLoading(false);
    }
  }, [listingId]);

  /**
   * Charger les reviews depuis l'API
   */
  const loadReviews = async (page = 1, isInitial = false) => {
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      console.log('[ListingReviewsList] Fetching reviews for listingId:', listingId, 'page:', page);

      const response = await api.get(`/reviews/listings/${listingId}`, {
        params: {
          page,
          limit: pagination.limit
        }
      });

      console.log('[ListingReviewsList] API Response:', response.data);

      // La structure de réponse est : response.data.data contient { data: [], pagination: {} }
      const responseData = response.data.data || response.data;
      const reviewsData = responseData.data || responseData;
      const paginationData = responseData.pagination;

      console.log('[ListingReviewsList] Reviews data:', reviewsData);
      console.log('[ListingReviewsList] Pagination data:', paginationData);

      // Vérifier que data est bien un tableau
      const reviewsArray = Array.isArray(reviewsData) ? reviewsData : [];

      if (isInitial) {
        setReviews(reviewsArray);
      } else {
        setReviews(prev => [...prev, ...reviewsArray]);
      }

      setPagination(paginationData || {
        page: 1,
        limit: pagination.limit,
        total: 0,
        totalPages: 0
      });

    } catch (err) {
      console.error('[ListingReviewsList] Error loading reviews:', err);
      setError(t('reviews:errorLoading', 'Erreur lors du chargement des avis'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  /**
   * Charger plus de reviews
   */
  const handleLoadMore = () => {
    const nextPage = pagination.page + 1;
    loadReviews(nextPage, false);
  };

  /**
   * Toggle l'expansion d'une review
   */
  const toggleReviewExpansion = (reviewId) => {
    setExpandedReviews(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };

  /**
   * Formater la date
   */
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd MMMM yyyy', {
        locale: i18n.language === 'fr' ? fr : undefined
      });
    } catch (error) {
      return dateString;
    }
  };

  /**
   * Affichage d'une review individuelle
   */
  const ReviewItem = ({ review }) => {
    const isExpanded = expandedReviews.has(review.id);
    const hasOwnerResponse = Boolean(review.owner_response) && review.response_moderation_status === 'approved';
    const photos = Array.isArray(review.equipment_photos)
      ? review.equipment_photos.filter(Boolean)
      : [];
    const [activePhotoIndex, setActivePhotoIndex] = useState(0);

    useEffect(() => {
      setActivePhotoIndex(0);
    }, [review.id]);

    const computeAverageFromArray = (values) => {
      const numericValues = values
        .map(value => (value === null || value === undefined ? null : Number(value)))
        .filter(value => value !== null && !Number.isNaN(value));

      if (numericValues.length === 0) {
        return null;
      }

      const total = numericValues.reduce((sum, value) => sum + value, 0);
      return (total / numericValues.length).toFixed(1);
    };

    const equipmentAverageValue = review.equipment_rating !== undefined && review.equipment_rating !== null
      ? Number(review.equipment_rating).toFixed(1)
      : computeAverageFromArray([
          review.equipment_quality_rating,
          review.equipment_cleanliness_rating,
          review.equipment_state_rating
        ]);

    const processAverageValue = review.process_rating !== undefined && review.process_rating !== null
      ? Number(review.process_rating).toFixed(1)
      : computeAverageFromArray([
          review.process_pickup_rating,
          review.process_return_rating,
          review.process_communication_rating
        ]);

    const experienceAverageValue = review.experience_rating !== undefined && review.experience_rating !== null
      ? Number(review.experience_rating).toFixed(1)
      : computeAverageFromArray([review.experience_value_rating]);

    const summaryCards = [
      {
        key: 'equipment',
        label: t('reviews:categories.equipment', 'Équipement'),
        averageText: equipmentAverageValue,
        ratingValue: equipmentAverageValue ? Number(equipmentAverageValue) : null,
        gradient: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.light, 0.04)})`,
        border: alpha(theme.palette.primary.main, 0.25)
      },
      {
        key: 'process',
        label: t('reviews:categories.process', 'Processus'),
        averageText: processAverageValue,
        ratingValue: processAverageValue ? Number(processAverageValue) : null,
        gradient: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.12)}, ${alpha(theme.palette.success.light, 0.04)})`,
        border: alpha(theme.palette.success.main, 0.25)
      },
      {
        key: 'experience',
        label: t('reviews:categories.experience', 'Expérience'),
        averageText: experienceAverageValue,
        ratingValue: experienceAverageValue ? Number(experienceAverageValue) : null,
        gradient: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.12)}, ${alpha(theme.palette.warning.light, 0.04)})`,
        border: alpha(theme.palette.warning.main, 0.25)
      }
    ].filter(card => card.ratingValue !== null && !Number.isNaN(card.ratingValue));

    const formattedCreatedDate = formatDate(review.created_at);

    const getPhotoSource = (photo) => {
      if (!photo) {
        return '';
      }

      if (typeof photo === 'string') {
        return photo;
      }

      if (typeof photo === 'object') {
        return photo.url || photo.src || photo.path || '';
      }

      return '';
    };

    const handlePrevPhoto = (event) => {
      event.stopPropagation();
      if (photos.length <= 1) {
        return;
      }
      setActivePhotoIndex(prev => (prev === 0 ? photos.length - 1 : prev - 1));
    };

    const handleNextPhoto = (event) => {
      event.stopPropagation();
      if (photos.length <= 1) {
        return;
      }
      setActivePhotoIndex(prev => (prev === photos.length - 1 ? 0 : prev + 1));
    };

    const handleThumbnailClick = (event, index) => {
      event.stopPropagation();
      if (index === activePhotoIndex) {
        return;
      }
      setActivePhotoIndex(index);
    };

    const currentPhotoSrc = getPhotoSource(photos[activePhotoIndex]);

    const renderRatingDetail = (label, ratingValue, comment, accentColor) => {
      if (ratingValue === undefined || ratingValue === null) {
        return null;
      }

      const numericValue = Number(ratingValue);
      if (Number.isNaN(numericValue)) {
        return null;
      }

      return (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: alpha(accentColor, 0.08),
            border: `1px solid ${alpha(accentColor, 0.2)}`
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={comment ? 0.75 : 0}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ flex: 1 }}>
              {label}
            </Typography>
            <Rating value={numericValue} readOnly size="small" precision={0.5} />
            <Typography variant="caption" color="text.secondary">
              {numericValue.toFixed(1)}
            </Typography>
          </Stack>
          {comment && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.85rem' }}>
              {comment}
            </Typography>
          )}
        </Box>
      );
    };

    return (
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          overflow: 'hidden',
          borderRadius: 3,
          position: 'relative',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.08)} 0%, ${theme.palette.background.paper} 65%)`,
          transition: 'transform 0.25s ease, box-shadow 0.25s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6
          }
        }}
      >
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})` }} />

        {/* En-tête condensé - toujours visible */}
        <Box
          sx={{
            p: 3,
            cursor: 'pointer',
            backgroundColor: alpha(theme.palette.background.default, 0.3)
          }}
          onClick={() => toggleReviewExpansion(review.id)}
        >
          <Stack 
            direction={isMobile ? 'column' : 'row'} 
            spacing={2} 
            alignItems="flex-start"
            sx={{ position: 'relative' }}
          >
            {/* Avatar du reviewer */}
            <Avatar
              src={review.reviewer?.profilePicture}
              alt={review.reviewer?.name}
              sx={{ width: 56, height: 56, boxShadow: 3 }}
            >
              {review.reviewer?.name?.charAt(0) || 'R'}
            </Avatar>

            {/* Infos principales */}
            <Box sx={{ flex: 1, minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
              <Stack
                direction={isMobile ? 'column' : 'row'}
                justifyContent="space-between"
                alignItems={isMobile ? 'flex-start' : 'center'}
                flexWrap="wrap"
                rowGap={1.5}
                columnGap={1.5}
              >
                <Stack spacing={0.5} sx={{ minWidth: 0, width: '100%' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      whiteSpace: 'normal',
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word'
                    }}
                  >
                    {review.reviewer?.name || t('reviews:anonymousReviewer', 'Utilisateur anonyme')}
                  </Typography>
                  {formattedCreatedDate && (
                    <Chip
                      label={formattedCreatedDate}
                      size="small"
                      sx={{ alignSelf: 'flex-start', backgroundColor: alpha(theme.palette.primary.main, 0.12), fontWeight: 500 }}
                    />
                  )}
                </Stack>
                
                {/* Note globale avec étoiles */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '999px',
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    flexShrink: 0,
                    maxWidth: '100%'
                  }}
                >
                  <Rating value={review.overall_rating || 0} readOnly precision={0.5} size="small" />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {(review.overall_rating || 0).toFixed(1)}
                  </Typography>
                </Box>
              </Stack>

              {/* Commentaire général - condensé */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
                }}
              >
                <Typography
                  variant="body1"
                  color="text.primary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: isExpanded || isMobile ? 'unset' : 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: isExpanded || isMobile ? 'visible' : 'hidden',
                    textOverflow: isExpanded || isMobile ? 'clip' : 'ellipsis',
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word'
                  }}
                >
                  {review.overall_comment || t('reviews:noComment', 'Aucun commentaire')}
                </Typography>
              </Box>

              {/* Badges rapides */}
              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                {review.would_recommend && (
                  <Chip
                    icon={<ThumbUpIcon />}
                    label={t('reviews:wouldRecommend', 'Recommande')}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
                {review.is_verified && (
                  <Chip
                    icon={<VerifiedIcon />}
                    label={t('reviews:verified', 'Vérifié')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {hasOwnerResponse && (
                  <Chip
                    icon={<ReplyIcon />}
                    label={t('reviews:ownerReplied', 'Réponse du propriétaire')}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>

            {/* Icône d'expansion */}
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                toggleReviewExpansion(review.id);
              }}
              sx={{
                mt: isMobile ? 0 : 0.5,
                position: isMobile ? 'absolute' : 'static',
                top: isMobile ? 0 : 'auto',
                right: isMobile ? 0 : 'auto',
                flexShrink: 0,
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2)
                }
              }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
        </Box>

        {/* Détails déployés */}
        <Collapse in={isExpanded}>
          <Divider sx={{ mx: 3 }} />
          
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {summaryCards.length > 0 && summaryCards.map(card => (
                <Grid item xs={12} sm={4} key={card.key}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      borderRadius: 2,
                      background: card.gradient,
                      border: `1px solid ${card.border}`,
                      height: '100%'
                    }}
                  >
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                      {card.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1, gap: 1 }}>
                      <Rating value={card.ratingValue} readOnly size="small" precision={0.1} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {card.averageText}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}

              {/* Sous-critères détaillés */}
              {/* Sous-critères équipement */}
              {(review.equipment_quality_rating || review.equipment_cleanliness_rating || review.equipment_state_rating) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('reviews:equipmentDetails', 'Détails équipement')}
                  </Typography>
                  <Stack spacing={1.5}>
                    {renderRatingDetail(
                      t('reviews:criteria.equipment_quality', 'Qualité de l\'équipement'),
                      review.equipment_quality_rating,
                      review.equipment_quality_comment,
                      theme.palette.primary.main
                    )}
                    {renderRatingDetail(
                      t('reviews:criteria.equipment_cleanliness', 'Propreté de l\'équipement'),
                      review.equipment_cleanliness_rating,
                      review.equipment_cleanliness_comment,
                      theme.palette.primary.main
                    )}
                    {renderRatingDetail(
                      t('reviews:criteria.equipment_state', 'État général de l\'équipement'),
                      review.equipment_state_rating,
                      review.equipment_state_comment,
                      theme.palette.primary.main
                    )}
                  </Stack>
                </Grid>
              )}

              {/* Sous-critères processus */}
              {(review.process_pickup_rating || review.process_return_rating || review.process_communication_rating) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('reviews:processDetails', 'Détails processus')}
                  </Typography>
                  <Stack spacing={1.5}>
                    {renderRatingDetail(
                      t('reviews:criteria.process_pickup', 'Processus de récupération'),
                      review.process_pickup_rating,
                      review.process_pickup_comment,
                      theme.palette.success.main
                    )}
                    {renderRatingDetail(
                      t('reviews:criteria.process_return', 'Processus de retour'),
                      review.process_return_rating,
                      review.process_return_comment,
                      theme.palette.success.main
                    )}
                    {renderRatingDetail(
                      t('reviews:criteria.process_communication', 'Qualité de communication'),
                      review.process_communication_rating,
                      review.process_communication_comment,
                      theme.palette.success.main
                    )}
                  </Stack>
                </Grid>
              )}

              {/* Sous-critères expérience */}
              {review.experience_value_rating && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('reviews:experienceDetails', 'Détails expérience')}
                  </Typography>
                  <Stack spacing={1.5}>
                    {renderRatingDetail(
                      t('reviews:criteria.experience_value', 'Rapport qualité/prix'),
                      review.experience_value_rating,
                      review.experience_value_comment,
                      theme.palette.warning.main
                    )}
                  </Stack>
                </Grid>
              )}

              {/* Photos de l'équipement */}
              {photos.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    <ImageIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {t('reviews:photos', 'Photos')}
                  </Typography>
                  <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                    <Box
                      component="img"
                      src={currentPhotoSrc}
                      alt={`Review photo ${activePhotoIndex + 1}`}
                      sx={{
                        display: 'block',
                        width: '100%',
                        maxHeight: 340,
                        objectFit: 'cover',
                        backgroundColor: alpha(theme.palette.common.black, 0.04)
                      }}
                    />

                    {photos.length > 1 && (
                      <>
                        <IconButton
                          size="small"
                          onClick={handlePrevPhoto}
                          disabled={photos.length <= 1}
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: 12,
                            transform: 'translateY(-50%)',
                            color: 'primary.contrastText',
                            backgroundColor: alpha(theme.palette.common.black, 0.45),
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.common.black, 0.6)
                            },
                            '&.Mui-disabled': {
                              backgroundColor: alpha(theme.palette.common.black, 0.25),
                              color: alpha(theme.palette.common.white, 0.6)
                            }
                          }}
                        >
                          <ChevronLeftIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleNextPhoto}
                          disabled={photos.length <= 1}
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            right: 12,
                            transform: 'translateY(-50%)',
                            color: 'primary.contrastText',
                            backgroundColor: alpha(theme.palette.common.black, 0.45),
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.common.black, 0.6)
                            },
                            '&.Mui-disabled': {
                              backgroundColor: alpha(theme.palette.common.black, 0.25),
                              color: alpha(theme.palette.common.white, 0.6)
                            }
                          }}
                        >
                          <ChevronRightIcon />
                        </IconButton>
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 12,
                            right: 16,
                            bgcolor: alpha(theme.palette.common.black, 0.55),
                            color: '#fff',
                            px: 1,
                            py: 0.25,
                            borderRadius: 999,
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                        >
                          {activePhotoIndex + 1} / {photos.length}
                        </Box>
                      </>
                    )}
                  </Box>

                  {photos.length > 1 && (
                    <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap">
                      {photos.map((photo, idx) => {
                        const thumbSrc = getPhotoSource(photo);
                        return (
                          <Box
                            key={`${review.id}-photo-${idx}`}
                            onClick={(event) => handleThumbnailClick(event, idx)}
                            sx={{
                              position: 'relative',
                              width: 76,
                              height: 56,
                              borderRadius: 1,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              outline: idx === activePhotoIndex
                                ? `2px solid ${theme.palette.primary.main}`
                                : `1px solid ${alpha(theme.palette.common.black, 0.15)}`,
                              boxShadow: idx === activePhotoIndex ? 4 : 0,
                              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: 3
                              }
                            }}
                          >
                            <Box
                              component="img"
                              src={thumbSrc}
                              alt={`Thumbnail ${idx + 1}`}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Grid>
              )}

              {/* Tags positifs et négatifs */}
              {(review.positive_tags?.length > 0 || review.negative_tags?.length > 0) && (
                <Grid item xs={12}>
                  {review.positive_tags?.length > 0 && (
                    <Box
                      sx={{
                        mb: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.success.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="success.main" sx={{ display: 'block', mb: 0.75 }}>
                        ✅ {t('reviews:positivePoints', 'Points forts')}
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap">
                        {review.positive_tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={t(`reviews:tags.${tag}`, tag)}
                            size="small"
                            sx={{
                              mb: 0.5,
                              height: 22,
                              fontSize: '0.75rem',
                              backgroundColor: alpha(theme.palette.success.main, 0.2),
                              color: theme.palette.success.dark,
                              fontWeight: 600
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                  
                  {review.negative_tags?.length > 0 && (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.error.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="error.main" sx={{ display: 'block', mb: 0.75 }}>
                        ⚠️ {t('reviews:negativePoints', 'Points à améliorer')}
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap">
                        {review.negative_tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={t(`reviews:tags.${tag}`, tag)}
                            size="small"
                            sx={{
                              mb: 0.5,
                              height: 22,
                              fontSize: '0.75rem',
                              backgroundColor: alpha(theme.palette.error.main, 0.2),
                              color: theme.palette.error.dark,
                              fontWeight: 600
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Grid>
              )}

              {/* Recommandations */}
              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  {review.would_recommend !== null && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        backgroundColor: review.would_recommend
                          ? alpha(theme.palette.success.main, 0.12)
                          : alpha(theme.palette.error.main, 0.12),
                        border: `1px solid ${alpha(review.would_recommend ? theme.palette.success.main : theme.palette.error.main, 0.3)}`
                      }}
                    >
                      {review.would_recommend ? (
                        <ThumbUpIcon color="success" fontSize="small" />
                      ) : (
                        <ThumbDownIcon color="error" fontSize="small" />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {review.would_recommend
                          ? t('reviews:wouldRecommendYes', 'Recommande ce propriétaire')
                          : t('reviews:wouldRecommendNo', 'Ne recommande pas')}
                      </Typography>
                    </Box>
                  )}
                  {review.would_rent_again !== null && review.would_rent_again && (
                    <Chip
                      label={t('reviews:wouldRentAgain', 'Louerait à nouveau')}
                      size="small"
                      color="success"
                      variant="filled"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>

            {/* Réponse du propriétaire */}
            {hasOwnerResponse && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.12)}, ${alpha(theme.palette.info.light, 0.05)})`,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <ReplyIcon color="info" fontSize="small" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'info.main' }}>
                      {t('reviews:ownerResponse', 'Réponse du propriétaire')}
                    </Typography>
                    {review.owner_response_date && (
                      <Typography variant="caption" color="text.secondary">
                        • {formatDate(review.owner_response_date)}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.primary" sx={{ fontStyle: 'italic' }}>
                    {review.owner_response}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Collapse>
      </Paper>
    );
  };

  // Vérification du listingId
  if (!listingId) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {t('reviews:noListingId', 'Aucun identifiant de listing fourni')}
        </Typography>
      </Paper>
    );
  }

  // Affichage du chargement initial
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Affichage d'une erreur
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  // Aucune review
  if (reviews.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {t('reviews:noReviews', 'Aucun avis pour le moment')}
        </Typography>
      </Paper>
    );
  }

  // Affichage des reviews
  return (
    <Box>
      {/* Liste des reviews */}
      {reviews.map(review => (
        <ReviewItem key={review.id} review={review} />
      ))}

      {/* Bouton Load More */}
      {pagination.page < pagination.totalPages && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={handleLoadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={20} /> : null}
          >
            {loadingMore
              ? t('reviews:loading', 'Chargement...')
              : t('reviews:loadMore', 'Charger plus d\'avis')}
          </Button>
        </Box>
      )}

      {/* Indicateur de pagination */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
        {t('reviews:showing', 'Affichage de {{current}} sur {{total}} avis', {
          current: reviews.length,
          total: pagination.total
        })}
      </Typography>
    </Box>
  );
};

export default ListingReviewsList;
